export const SITE_URL = "https://fly4me.ca";
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image-v2.jpg`;

type BreadcrumbItem = {
  name: string;
  path: string;
};

export function buildCanonicalUrl(path: string): string {
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${SITE_URL}${cleanPath === "/" ? "" : cleanPath}`;
}

export function buildBreadcrumbSchema(items: BreadcrumbItem[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: buildCanonicalUrl(item.path),
    })),
  };
}

export function buildOrganizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Fly4MEdia",
    url: SITE_URL,
    logo: `${SITE_URL}/favicon.png`,
    sameAs: [
      "https://instagram.com/fly4media",
      "https://vimeo.com/fly4media",
      "https://youtube.com/@fly4media",
    ],
  };
}

/**
 * ONE BUSINESS, ONE @id.
 *
 * This module used to define a parallel identity — `LocalBusiness` at
 * `#localbusiness`, with a hardcoded nine-community areaServed and a stale
 * email — while the prerendered graph defines the authoritative
 * `ProfessionalService` at `#business` with the full roster. Two entities for
 * one company is precisely the split that makes an answer engine unsure which
 * facts to trust. The prerenderer strips runtime JSON-LD from static HTML, so
 * this node only ever appears on client-side navigations — but there it must
 * REFERENCE the canonical entity, not compete with it. Slim by design: detail
 * lives in the static graph.
 */
export function buildLocalBusinessSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "@id": `${SITE_URL}/#business`,
    name: "Fly4MEdia",
    url: SITE_URL,
    image: DEFAULT_OG_IMAGE,
    address: {
      "@type": "PostalAddress",
      addressLocality: "Calgary",
      addressRegion: "AB",
      addressCountry: "CA",
    },
    priceRange: "$$",
  };
}

export function buildServiceSchema({
  name,
  description,
  path,
  areas,
}: {
  name: string;
  description: string;
  path: string;
  areas: string[];
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name,
    description,
    provider: {
      "@id": `${SITE_URL}/#business`,
      name: "Fly4MEdia",
    },
    areaServed: areas,
    url: buildCanonicalUrl(path),
    serviceType: [
      "Drone photography",
      "Aerial cinematography",
      "Real estate drone video",
      "FPV drone filming",
    ],
  };
}

export function buildFaqSchema(
  questions: Array<{ question: string; answer: string }>,
) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: questions.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };
}
